/**
 * CRM Navigation Context
 *
 * Manages the navigation state for the Cloze-inspired CRM interface:
 * - Navigation mode toggle (tabs/sidebar/hidden)
 * - Sidebar collapse state
 * - Panel widths for three-panel layout
 * - Persists preferences to localStorage and Firestore
 */

import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useCallback,
  useMemo,
  ReactNode,
} from 'react';
import {
  CRMNavigationState,
  CRMNavigationContextType,
  CRMNavigationMode,
  DEFAULT_NAVIGATION_STATE,
  CRM_STORAGE_KEYS,
  DEFAULT_PANEL_WIDTHS,
} from '../types/crmNavigation.types';
import { useAuth } from './AuthContext';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { db } from '../services/firebase/config';

// Action types
type NavigationAction =
  | { type: 'SET_MODE'; payload: CRMNavigationMode }
  | { type: 'TOGGLE_SIDEBAR' }
  | { type: 'SET_SIDEBAR_COLLAPSED'; payload: boolean }
  | { type: 'SET_ACTIVE_ITEM'; payload: string | null }
  | { type: 'TOGGLE_EXPANDED_GROUP'; payload: string }
  | { type: 'SET_PANEL_WIDTH'; payload: { panel: 'left' | 'right'; width: number } }
  | { type: 'TOGGLE_PANEL'; payload: 'left' | 'right' }
  | { type: 'SET_IS_MOBILE'; payload: boolean }
  | { type: 'SET_TRANSITIONING'; payload: boolean }
  | { type: 'RESET_TO_DEFAULTS' }
  | { type: 'HYDRATE_STATE'; payload: Partial<CRMNavigationState> };

// Reducer
function navigationReducer(
  state: CRMNavigationState,
  action: NavigationAction
): CRMNavigationState {
  switch (action.type) {
    case 'SET_MODE':
      return { ...state, mode: action.payload };

    case 'TOGGLE_SIDEBAR':
      return {
        ...state,
        sidebar: {
          ...state.sidebar,
          isCollapsed: !state.sidebar.isCollapsed,
        },
      };

    case 'SET_SIDEBAR_COLLAPSED':
      return {
        ...state,
        sidebar: {
          ...state.sidebar,
          isCollapsed: action.payload,
        },
      };

    case 'SET_ACTIVE_ITEM':
      return {
        ...state,
        sidebar: {
          ...state.sidebar,
          activeItemId: action.payload,
        },
      };

    case 'TOGGLE_EXPANDED_GROUP': {
      const { expandedGroups } = state.sidebar;
      const groupId = action.payload;
      const isExpanded = expandedGroups.includes(groupId);
      return {
        ...state,
        sidebar: {
          ...state.sidebar,
          expandedGroups: isExpanded
            ? expandedGroups.filter((id) => id !== groupId)
            : [...expandedGroups, groupId],
        },
      };
    }

    case 'SET_PANEL_WIDTH': {
      const { panel, width } = action.payload;
      const panelKey = panel === 'left' ? 'leftPanel' : 'rightPanel';
      const limits = panel === 'left' ? DEFAULT_PANEL_WIDTHS.leftPanel : DEFAULT_PANEL_WIDTHS.rightPanel;
      const clampedWidth = Math.max(limits.min, Math.min(limits.max, width));
      return {
        ...state,
        panels: {
          ...state.panels,
          [panelKey]: {
            ...state.panels[panelKey],
            width: clampedWidth,
          },
        },
      };
    }

    case 'TOGGLE_PANEL': {
      const panelKey = action.payload === 'left' ? 'leftPanel' : 'rightPanel';
      return {
        ...state,
        panels: {
          ...state.panels,
          [panelKey]: {
            ...state.panels[panelKey],
            collapsed: !state.panels[panelKey].collapsed,
          },
        },
      };
    }

    case 'SET_IS_MOBILE':
      return { ...state, isMobile: action.payload };

    case 'SET_TRANSITIONING':
      return { ...state, isTransitioning: action.payload };

    case 'RESET_TO_DEFAULTS':
      return DEFAULT_NAVIGATION_STATE;

    case 'HYDRATE_STATE':
      return { ...state, ...action.payload };

    default:
      return state;
  }
}

// Create context
const CRMNavigationContext = createContext<CRMNavigationContextType | null>(null);

// Provider props
interface CRMNavigationProviderProps {
  children: ReactNode;
}

// Provider component
export function CRMNavigationProvider({ children }: CRMNavigationProviderProps) {
  const { user, userProfile } = useAuth();
  const [state, dispatch] = useReducer(navigationReducer, DEFAULT_NAVIGATION_STATE);

  // Load preferences from localStorage on mount
  useEffect(() => {
    try {
      const storedMode = localStorage.getItem(CRM_STORAGE_KEYS.navigationMode);
      const storedCollapsed = localStorage.getItem(CRM_STORAGE_KEYS.sidebarCollapsed);
      const storedPanelWidths = localStorage.getItem(CRM_STORAGE_KEYS.panelWidths);
      const storedExpandedGroups = localStorage.getItem(CRM_STORAGE_KEYS.expandedGroups);

      const hydratedState: Partial<CRMNavigationState> = {};

      if (storedMode && ['tabs', 'sidebar', 'hidden'].includes(storedMode)) {
        hydratedState.mode = storedMode as CRMNavigationMode;
      }

      if (storedCollapsed !== null) {
        hydratedState.sidebar = {
          ...DEFAULT_NAVIGATION_STATE.sidebar,
          isCollapsed: storedCollapsed === 'true',
        };
      }

      if (storedPanelWidths) {
        try {
          const parsed = JSON.parse(storedPanelWidths);
          hydratedState.panels = {
            leftPanel: {
              width: parsed.left || DEFAULT_PANEL_WIDTHS.leftPanel.default,
              collapsed: false,
            },
            rightPanel: {
              width: parsed.right || DEFAULT_PANEL_WIDTHS.rightPanel.default,
              collapsed: false,
            },
          };
        } catch {
          // Ignore parse errors
        }
      }

      if (storedExpandedGroups) {
        try {
          const parsed = JSON.parse(storedExpandedGroups);
          if (Array.isArray(parsed)) {
            hydratedState.sidebar = {
              ...hydratedState.sidebar,
              ...DEFAULT_NAVIGATION_STATE.sidebar,
              expandedGroups: parsed,
            };
          }
        } catch {
          // Ignore parse errors
        }
      }

      if (Object.keys(hydratedState).length > 0) {
        dispatch({ type: 'HYDRATE_STATE', payload: hydratedState });
      }
    } catch (error) {
      console.error('Error loading CRM navigation preferences:', error);
    }
  }, []);

  // Load preferences from Firestore when user logs in
  useEffect(() => {
    async function loadUserPreferences() {
      if (!user?.uid) return;

      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          const data = userDoc.data();
          const crmPrefs = data.crmPreferences;

          if (crmPrefs) {
            const hydratedState: Partial<CRMNavigationState> = {};

            if (crmPrefs.navigationMode) {
              hydratedState.mode = crmPrefs.navigationMode;
            }

            if (typeof crmPrefs.sidebarCollapsed === 'boolean') {
              hydratedState.sidebar = {
                ...state.sidebar,
                isCollapsed: crmPrefs.sidebarCollapsed,
              };
            }

            if (crmPrefs.panelWidths) {
              hydratedState.panels = {
                leftPanel: {
                  width: crmPrefs.panelWidths.left || state.panels.leftPanel.width,
                  collapsed: state.panels.leftPanel.collapsed,
                },
                rightPanel: {
                  width: crmPrefs.panelWidths.right || state.panels.rightPanel.width,
                  collapsed: state.panels.rightPanel.collapsed,
                },
              };
            }

            if (Object.keys(hydratedState).length > 0) {
              dispatch({ type: 'HYDRATE_STATE', payload: hydratedState });
            }
          }
        }
      } catch (error) {
        console.error('Error loading CRM preferences from Firestore:', error);
      }
    }

    loadUserPreferences();
  }, [user?.uid]);

  // Persist to localStorage when state changes
  useEffect(() => {
    try {
      localStorage.setItem(CRM_STORAGE_KEYS.navigationMode, state.mode);
      localStorage.setItem(CRM_STORAGE_KEYS.sidebarCollapsed, String(state.sidebar.isCollapsed));
      localStorage.setItem(
        CRM_STORAGE_KEYS.panelWidths,
        JSON.stringify({
          left: state.panels.leftPanel.width,
          right: state.panels.rightPanel.width,
        })
      );
      localStorage.setItem(
        CRM_STORAGE_KEYS.expandedGroups,
        JSON.stringify(state.sidebar.expandedGroups)
      );
    } catch (error) {
      console.error('Error saving CRM navigation preferences to localStorage:', error);
    }
  }, [state.mode, state.sidebar.isCollapsed, state.panels, state.sidebar.expandedGroups]);

  // Save to Firestore (debounced) - use setDoc with merge to handle missing documents
  const saveToFirestore = useCallback(
    async (preferences: Record<string, unknown>) => {
      if (!user?.uid) return;

      try {
        await setDoc(
          doc(db, 'users', user.uid),
          { crmPreferences: preferences },
          { merge: true }
        );
      } catch (error) {
        console.error('Error saving CRM preferences to Firestore:', error);
      }
    },
    [user?.uid]
  );

  // Detect mobile viewport
  useEffect(() => {
    const checkMobile = () => {
      const isMobile = window.innerWidth < 768;
      dispatch({ type: 'SET_IS_MOBILE', payload: isMobile });

      // Auto-collapse sidebar on mobile
      if (isMobile && !state.sidebar.isCollapsed) {
        dispatch({ type: 'SET_SIDEBAR_COLLAPSED', payload: true });
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Actions
  const setMode = useCallback(
    (mode: CRMNavigationMode) => {
      dispatch({ type: 'SET_TRANSITIONING', payload: true });
      dispatch({ type: 'SET_MODE', payload: mode });

      // Save to Firestore
      saveToFirestore({ navigationMode: mode });

      // End transition after animation
      setTimeout(() => {
        dispatch({ type: 'SET_TRANSITIONING', payload: false });
      }, 300);
    },
    [saveToFirestore]
  );

  const toggleSidebar = useCallback(() => {
    dispatch({ type: 'TOGGLE_SIDEBAR' });
    saveToFirestore({ sidebarCollapsed: !state.sidebar.isCollapsed });
  }, [saveToFirestore, state.sidebar.isCollapsed]);

  const setSidebarCollapsed = useCallback(
    (collapsed: boolean) => {
      dispatch({ type: 'SET_SIDEBAR_COLLAPSED', payload: collapsed });
      saveToFirestore({ sidebarCollapsed: collapsed });
    },
    [saveToFirestore]
  );

  const setActiveItem = useCallback((itemId: string | null) => {
    dispatch({ type: 'SET_ACTIVE_ITEM', payload: itemId });
  }, []);

  const toggleExpandedGroup = useCallback((groupId: string) => {
    dispatch({ type: 'TOGGLE_EXPANDED_GROUP', payload: groupId });
  }, []);

  const setPanelWidth = useCallback(
    (panel: 'left' | 'right', width: number) => {
      dispatch({ type: 'SET_PANEL_WIDTH', payload: { panel, width } });
      saveToFirestore({
        panelWidths: {
          left: panel === 'left' ? width : state.panels.leftPanel.width,
          right: panel === 'right' ? width : state.panels.rightPanel.width,
        },
      });
    },
    [saveToFirestore, state.panels]
  );

  const togglePanel = useCallback((panel: 'left' | 'right') => {
    dispatch({ type: 'TOGGLE_PANEL', payload: panel });
  }, []);

  const resetToDefaults = useCallback(() => {
    dispatch({ type: 'RESET_TO_DEFAULTS' });
    saveToFirestore({
      navigationMode: 'tabs',
      sidebarCollapsed: false,
      panelWidths: {
        left: DEFAULT_PANEL_WIDTHS.leftPanel.default,
        right: DEFAULT_PANEL_WIDTHS.rightPanel.default,
      },
    });
  }, [saveToFirestore]);

  // Memoized context value
  const contextValue = useMemo<CRMNavigationContextType>(
    () => ({
      ...state,
      setMode,
      toggleSidebar,
      setSidebarCollapsed,
      setActiveItem,
      toggleExpandedGroup,
      setPanelWidth,
      togglePanel,
      resetToDefaults,
    }),
    [
      state,
      setMode,
      toggleSidebar,
      setSidebarCollapsed,
      setActiveItem,
      toggleExpandedGroup,
      setPanelWidth,
      togglePanel,
      resetToDefaults,
    ]
  );

  return (
    <CRMNavigationContext.Provider value={contextValue}>
      {children}
    </CRMNavigationContext.Provider>
  );
}

// Hook to use the context
export function useCRMNavigation(): CRMNavigationContextType {
  const context = useContext(CRMNavigationContext);
  if (!context) {
    throw new Error('useCRMNavigation must be used within a CRMNavigationProvider');
  }
  return context;
}

// Optional hook that returns null if not in provider (for components that may be outside CRM)
export function useCRMNavigationOptional(): CRMNavigationContextType | null {
  return useContext(CRMNavigationContext);
}

export default CRMNavigationContext;
